/**
 * Prompt → chat morph store.
 *
 * Drives a single floating textarea element that is rendered ABOVE the router
 * (see `PromptMorphOverlay`), so it survives the route change from the
 * Landing/Dashboard prompt to the Workspace — bridging the code-split chunk
 * load with no blank flash. The source page seeds it at the prompt's rect; the
 * workspace then moves it onto the real chat input and fades it out, so it
 * reads as the same textarea travelling into place.
 *
 * @module
 */

import { useSyncExternalStore } from 'react'

/** Viewport rectangle for the floating textarea. */
export interface MorphRect {
  left: number
  top: number
  width: number
  height: number
}

interface MorphState {
  /** Whether the overlay is mounted. */
  active: boolean
  /** Prompt text shown inside the floating textarea. */
  text: string
  /** Current rect (start = prompt box, then the chat input). */
  rect: MorphRect | null
  /** Whether to animate to the current rect (false for the initial placement). */
  animate: boolean
  /** Whether the overlay is fading out (handing off to the real input). */
  fading: boolean
}

const IDLE: MorphState = { active: false, text: '', rect: null, animate: false, fading: false }
let state: MorphState = IDLE
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): MorphState {
  return state
}

/** Begin a morph: place the floating textarea at the source prompt's rect. */
export function beginPromptMorph(rect: MorphRect, text: string): void {
  state = { active: true, text, rect, animate: false, fading: false }
  emit()
}

/**
 * Runs `fn` once the visual viewport has settled enough to measure a morph
 * source rect.
 *
 * On coarse-pointer devices the submit that starts a morph usually happens with
 * the soft keyboard open; measuring then captures a rect against a shifted /
 * shrunken visual viewport, and the fixed-position overlay lands visibly offset
 * once the keyboard dismisses. Blur the focused prompt first and wait two
 * animation frames for the viewport to settle before measuring. On fine-pointer
 * (desktop) devices there is no soft keyboard, so `fn` runs synchronously and
 * the morph is byte-identical to the pre-existing behavior.
 *
 * @param blurTarget - The focused prompt element to blur (if any).
 * @param fn - Measures the source rect and begins the morph.
 */
export function settleViewportForMorph(
  blurTarget: HTMLElement | null | undefined,
  fn: () => void,
): void {
  const coarse =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  if (!coarse) {
    fn()
    return
  }
  blurTarget?.blur()
  requestAnimationFrame(() => requestAnimationFrame(fn))
}

/** Animate the floating textarea to a new rect (the chat input's slot). */
export function movePromptMorph(rect: MorphRect): void {
  if (!state.active) return
  state = { ...state, rect, animate: true }
  emit()
}

/** Fade the floating textarea out as the real chat input takes over. */
export function fadePromptMorph(): void {
  if (!state.active) return
  state = { ...state, fading: true }
  emit()
}

/** Tear the overlay down. */
export function endPromptMorph(): void {
  if (!state.active) return
  state = IDLE
  emit()
}

/** Subscribe to the morph state from a component. */
export function usePromptMorph(): MorphState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Read the current state imperatively (e.g. to decide whether a morph is in flight). */
export function getPromptMorph(): MorphState {
  return state
}
